import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CloseIcon, DeleteIcon, EditIcon, Menu } from "app/components/Icons";
import PlusIcon from "app/components/Icons/PlusIcon";
import checkmarkDouble from "assets/svgs/checkmark-double.svg";
import collapseIcon from "assets/svgs/collapse.svg";
import invisibleIcon from "assets/svgs/invisible.svg";
import { ChevronRightIcon } from "lucide-react";
import React from "react";
import { GroupAction, GroupByTypeEnum } from "../../../types";
import { TicketGroup } from "../../Tickets/types";
import HiddenGroupItem from "./ColumnComponents/HiddenGroups/HiddenGroupItem";
import InfoText from "./ColumnComponents/HiddenGroups/InfoText";

interface props {
  handleSelect: (type: GroupAction) => void;
  isSelected: boolean;
  groupBy: GroupByTypeEnum;
  hiddenGroups: TicketGroup[];
  setIsGroupModalOpen: (value: React.SetStateAction<boolean>) => void;
  onUnHideGroup: (group: TicketGroup) => void;
  groupId: string;
}

const ColumnGroupActionsDropdown: React.FC<props> = ({
  handleSelect,
  isSelected,
  groupBy,
  hiddenGroups,
  setIsGroupModalOpen,
  onUnHideGroup,
  groupId,
}) => {
  const options = [
    {
      icon: (
        <img src={collapseIcon} alt="collapseIcon" className="size-[20px]" />
      ),
      label: "Collapse group",
      type: GroupAction.COLLAPSE,
    },
    isSelected
      ? {
          icon: <CloseIcon color="#79808A" />,
          label: "Unselect all",
          type: GroupAction.Un_SELECT_ALL,
        }
      : {
          icon: (
            <img
              src={checkmarkDouble}
              alt="checkmarkDouble"
              className="size-[20px]"
            />
          ),
          label: "Select all",
          type: GroupAction.SELECT_ALL,
        },
    {
      icon: (
        <img src={invisibleIcon} alt="invisibleIcon" className="size-[20px]" />
      ),
      label: "Hide group",
      type: GroupAction.HIDE,
    },
    groupBy === GroupByTypeEnum.CUSTOM_TITLES
      ? {
          icon: <EditIcon size={20} color="#79808A" />,
          label: "Edit group details",
          type: GroupAction.EDIT,
        }
      : null,
    groupBy === GroupByTypeEnum.CUSTOM_TITLES &&
    groupId.toLowerCase() !== "no custom title" &&
    groupId.length > 0
      ? {
          icon: <DeleteIcon size={20} color="#79808A" />,
          label: "Delete group",
          type: GroupAction.REMOVE,
        }
      : null,
  ];

  return (
    <div className=" relative">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <button className="flex flex-row items-center  rounded outline-none">
            <Menu />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className="w-64 bg-white rounded-[4px] border border-gray-100 shadow-md py-[10px] z-40"
            sideOffset={5}
          >
            {options.filter(Boolean).map((ele) => {
              return (
                <DropdownMenu.Item
                  key={ele?.type}
                  className="items-center flex gap-2 pl-4 pr-[8px] py-[12px] text-sm leading-[21px] mt-[2px] text-secondary outline-none cursor-pointer rounded hover:bg-gray-100 hover:rounded-none focus:bg-gray-400"
                  onClick={() => handleSelect(ele?.type as GroupAction)}
                >
                  <div className={`flex items-start gap-3 p-2 w-[100%]`}>
                    <div className="min-w-[20px]">{ele?.icon}</div>
                    <div className="text-sm leading-[18px] ita font-normal text-[#79808A] text-ellipsis overflow-hidden whitespace-nowrap">
                      {ele?.label}
                    </div>
                  </div>
                </DropdownMenu.Item>
              );
            })}
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className="group relative  select-none data-[disabled]:pointer-events-none data-[highlighted]:bg-violet9 data-[highlighted]:data-[state=open]:bg-violet9 data-[state=open]:bg-violet4 data-[disabled]:text-mauve8 data-[highlighted]:data-[state=open]:text-violet1 data-[highlighted]:text-violet1 data-[state=open]:text-violet11 items-center flex gap-2 pl-4 pr-[8px] py-[12px] text-sm leading-[21px] mt-[2px] text-secondary outline-none cursor-pointer rounded hover:bg-gray-100 hover:rounded-none focus:bg-gray-100">
                <div className={`flex items-start gap-3 p-2 w-[100%]`}>
                  <div className="min-w-[20px]">
                    {" "}
                    <PlusIcon color="#79808A" size={20} />
                  </div>
                  <div className="text-sm leading-[18px] ita font-normal text-[#79808A] text-ellipsis overflow-hidden whitespace-nowrap">
                    Add Group
                  </div>
                </div>
                <div className="ml-auto pl-5 text-[#79808A] opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <ChevronRightIcon />
                </div>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  className="min-w-[220px] rounded-md bg-white p-[5px] shadow-md will-change-[opacity,transform] data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade z-[999]"
                  sideOffset={2}
                  alignOffset={-5}
                >
                  <InfoText
                    groupBy={groupBy}
                    onAddNewGroup={() => setIsGroupModalOpen(true)}
                    hasDataVisible={hiddenGroups.length > 0}
                  />
                  {hiddenGroups?.map((group) => {
                    return (
                      <DropdownMenu.Item
                        key={group?._id}
                        onClick={() => onUnHideGroup(group)}
                        className="items-center flex gap-2 pl-4 pr-[8px] py-[12px] text-sm leading-[21px] mt-[2px] text-secondary outline-none cursor-pointer rounded hover:bg-gray-100 hover:rounded-none focus:bg-gray-100"
                      >
                        <HiddenGroupItem group={group} groupBy={groupBy} />,
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

export default ColumnGroupActionsDropdown;
